import React from "react";
import { Box, Typography, keyframes, Icon, Divider } from "@mui/material";
import { useTheme } from "@emotion/react";

// Staggered fade-in animation for calendar items
const fadeInUp = keyframes`
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
`;

const UnlimitedCalendarsIcon = ({ variant = "light" }) => {
  const theme = useTheme();
  const { primary, overlay } = theme.palette;
  const isDark = variant === "dark";

  // Colors based on variant - all grayscale
  const placeholderColor = isDark ? overlay.white30 : "rgba(255, 255, 255, 0.3)";
  const iconColor = isDark ? overlay.white50 : "rgba(255, 255, 255, 0.6)";
  const checkboxColors = [
    "rgba(158, 158, 158, 0.8)", // Grey
    "rgba(100, 181, 246, 0.8)", // Blue
    "rgba(129, 199, 132, 0.8)", // Green
    "rgba(239, 154, 154, 0.8)", // Red
    "rgba(186, 104, 200, 0.8)", // Purple
  ];
  const edgeLineColor = "rgba(255, 255, 255, 0.3)";
  const dividerColor = "rgba(255, 255, 255, 0.1)";
  const backgroundColor = isDark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.4)";
  const trayBgColor = isDark ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.95)";
  const trayLineColor = "rgba(255, 255, 255, 0.2)";

  const calendarItems = Array.from({ length: 5 }, (_, index) => ({
    id: index,
    width: index === 0 ? "70%" : index === 1 ? "50%" : index === 2 ? "85%" : index === 3 ? "60%" : "75%",
    delay: index * 0.2,
  }));

  return (
    <Box
      sx={{
        position: "relative",
        width: "200px",
        height: "140px",
        margin: "0 auto",
        backgroundColor: backgroundColor,
        backdropFilter: "blur(10px)",
        display: "flex",
        flexDirection: "column",
        padding: "16px",
      }}
    >
      {/* Left edge line */}
      <Box
        sx={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />
      
      {/* Right edge line */}
      <Box
        sx={{
          position: "absolute",
          right: 0,
          top: 0,
          bottom: 0,
          width: "1px",
          backgroundColor: edgeLineColor,
        }}
      />

      {/* Tray separator line (inside the padded area) */}
      <Box
        sx={{
          position: "absolute",
          left: "calc(75% - 8px)", // Adjust for padding
          top: "0",
          bottom: "0",
          width: "1px",
          backgroundColor: trayLineColor,
        }}
      />

      {/* Right side tray background (inside the padded area) */}
      <Box
        sx={{
          position: "absolute",
          right: "0",
          top: "0",
          bottom: "0",
          left: "calc(75% - 8px)", // Match the tray line position
          backgroundColor: trayBgColor,
        }}
      />

      {/* User Profile Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          mb: "8px",
          mt: "5px",
          width: "70%", // Constrain to left side of tray
        }}
      >
        {/* User Avatar - bigger */}
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            fontSize: "16px",
            color: iconColor,
            opacity: 0.8,
            fontWeight: 300,
            ml: "-2px"
          }}
        >
          account_circle
        </Icon>

        {/* User name placeholder - left aligned */}
        <Box
          sx={{
            height: "8px",
            backgroundColor: placeholderColor,
            borderRadius: "4px",
            width: "60%",
          }}
        />
      </Box>

      {/* Divider */}
      <Divider 
        sx={{ 
          borderColor: dividerColor, 
          mb: "12px",
          width: "70%", // Match the content width
        }} 
      />

      {/* Calendar List */}
      <Box sx={{ flex: 1, overflow: "hidden", width: "70%" }}>
        {calendarItems.map((item, index) => (
          <Box
            key={item.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "12px",
              mb: "8px",
              opacity: 0,
              animation: `${fadeInUp} 0.6s ease-out ${item.delay}s both`,
            }}
          >
            {/* Calendar Checkbox */}
            <Box
              sx={{
                position: "relative",
                width: "12px",
                height: "12px",
                backgroundColor: checkboxColors[index % checkboxColors.length],
                borderRadius: "2px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon
                baseClassName="material-symbols-outlined"
                sx={{
                  fontSize: "8px",
                  color: "white",
                  fontWeight: 600,
                }}
              >
                check
              </Icon>
            </Box>

            {/* Calendar name placeholder - left aligned */}
            <Box
              sx={{
                height: "6px",
                backgroundColor: placeholderColor,
                borderRadius: "3px",
                width: item.width,
              }}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default UnlimitedCalendarsIcon; 